import {
  Box,
  Divider,
  FlagIcon,
  FlagIconName,
  FlagIconNameType,
  Modal,
  SearchBar,
  Target,
  Text
} from "@modernatx-internal/component-library";
import React from "react";

import { useExperience } from "@/context/ExperienceContext";
import { useAvailableLanguages } from "@/hooks/useAvailableLanguages";
import { useCountrySearch } from "@/hooks/useCountrySearch";
import { Country } from "@/types/Country";
import { Language } from "@/types/Language";
import { getCountryName } from "@/utils/getCountryName";
import { languageCodeMap } from "@/utils/language";

import { useAvailableCountries } from "../../hooks/useAvailableCountries";

export interface CountryModalProps {
  allowCountries?: Country[];
  isOpen: boolean;
  noResultsText: React.ReactNode;
  onDismiss: () => void;
  searchLabel: React.ReactNode;
  title: React.ReactNode;
}

export const isValidIconCode = (code: string): code is FlagIconNameType => {
  return code.toUpperCase() in FlagIconName;
};

export const getFlagIcon = (countryCode: string): FlagIconNameType | undefined => {
  const upperCaseCode = countryCode.toUpperCase() as FlagIconNameType;
  if (isValidIconCode(upperCaseCode)) {
    return upperCaseCode;
  }
  return undefined;
};

const LocaleModalOption: React.FC<React.PropsWithChildren<{ href?: string }>> = ({
  children,
  href
}) => (
  <Target
    sx={{
      alignItems: "center",
      backgroundColor: "transparent",
      borderColor: "transparent",
      borderStyle: "solid",
      borderWidth: "1px",
      display: "flex",
      gap: 4,
      py: 4,
      paddingInlineStart: 2,
      transition: "transition-color-medium",
      ":hover": {
        backgroundColor: "field-surface-highlight"
      },
      ":active": {
        backgroundColor: "field-surface-highlight"
      },
      ":focus-visible": {
        borderRadius: "radius-2",
        borderColor: "focus"
      }
    }}
    href={href}
  >
    {children}
  </Target>
);

export const CountryModal: React.FC<CountryModalProps> = ({
  allowCountries,
  isOpen,
  noResultsText,
  onDismiss,
  searchLabel,
  title
}) => {
  const { country, alternates = [], language } = useExperience();
  const availableCountries = useAvailableCountries();
  const allowedCountries = React.useMemo(() => {
    if (allowCountries) {
      return new Set([...availableCountries].filter((c) => allowCountries.includes(c)));
    }
    return availableCountries;
  }, [allowCountries, availableCountries]);
  const { searchTerm, filteredCountries, handleSearchChange, clearSearch } =
    useCountrySearch(allowedCountries);
  const currentCountryIcon = country ? getFlagIcon(country) : undefined;
  const currentCountryName = React.useMemo(() => {
    if (!country) {
      return "";
    }
    return getCountryName(country, language);
  }, [country, language]);

  const getCountryHref = React.useMemo(() => {
    return (countryCode: Country) => {
      const newPath = alternates.find((alt) => alt.country === countryCode)?.urlWithCountryPathname;
      let href;
      if (newPath) {
        const url = new URL(newPath);
        href = url.pathname;
      }
      return href;
    };
  }, [alternates]);

  const handleDismiss = React.useCallback(() => {
    clearSearch();
    onDismiss();
  }, [clearSearch, onDismiss]);

  return (
    <Modal isOpen={isOpen} size="xs" title={title} onDismiss={handleDismiss}>
      <Box
        sx={{
          alignSelf: "start",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          pb: 5,
          width: "100%"
        }}
      >
        <Box sx={{ height: "100%" }}>
          <SearchBar label={searchLabel} value={searchTerm} onChange={handleSearchChange} />
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              gap: 4,
              paddingInlineStart: 2,
              py: 4
            }}
          >
            {currentCountryIcon && <FlagIcon flag={currentCountryIcon} />}
            <Text variant="body-1-medium">{currentCountryName}</Text>
          </Box>
          <Divider color="stroke-neutral-tertiary" />
          <Box sx={{ height: 80, overflowY: "auto" }}>
            {filteredCountries.length === 0 && (
              <Box sx={{ pt: 4 }}>
                <Text variant="body-1-medium">{noResultsText}</Text>
              </Box>
            )}
            {filteredCountries.map(({ countryCode, displayName }, index) => {
              const countryIcon = countryCode && getFlagIcon(countryCode);
              return (
                <Box key={index} onClick={handleDismiss}>
                  <LocaleModalOption href={getCountryHref(countryCode)}>
                    {countryIcon && <FlagIcon flag={countryIcon} />}
                    <Text variant="body-1-medium">{displayName}</Text>
                  </LocaleModalOption>
                </Box>
              );
            })}
          </Box>
          <Divider color="stroke-neutral-tertiary" />
        </Box>
      </Box>
    </Modal>
  );
};

export interface LanguageModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  title: React.ReactNode;
}

export const LanguageModal: React.FC<LanguageModalProps> = ({ title, isOpen, onDismiss }) => {
  const { alternates = [], country, language } = useExperience();

  const availableLanguages = useAvailableLanguages();

  const selectableLanguages = Array.from(availableLanguages).map((code) => ({
    code,
    name: languageCodeMap[code]
  }));

  const getLanguageHref = React.useMemo(() => {
    return (languageCode: Language) => {
      const newPath = alternates.find(
        (alt) => alt.country === country && alt.language === languageCode
      )?.urlWithLanguagePathname;
      let href;
      if (newPath) {
        const url = new URL(newPath);
        href = url.pathname;
      }
      return href;
    };
  }, [alternates, country]);

  return (
    <Modal isOpen={isOpen} size="xs" title={title} onDismiss={onDismiss}>
      <Box sx={{ pb: 5, height: "100%" }}>
        <Box>
          <Box sx={{ pb: 4, pt: 2, paddingInlineStart: 2 }}>
            {language && <Text variant="body-1-medium">{languageCodeMap[language]}</Text>}
          </Box>
          <Divider color="stroke-neutral-tertiary" />
          <Box sx={{ maxHeight: 80, overflowY: "auto" }}>
            {selectableLanguages.map(({ code, name }, index) => (
              <Box key={index} onClick={onDismiss}>
                <LocaleModalOption href={getLanguageHref(code)}>
                  <Text variant="body-1-medium">{name}</Text>
                </LocaleModalOption>
              </Box>
            ))}
          </Box>
          <Divider color="stroke-neutral-tertiary" />
        </Box>
      </Box>
    </Modal>
  );
};
